import * as openpgp from "openpgp";
import { getCompanyPrivateKeys } from "./companyContentKeys";
import { getActiveCompanyKey, getCompanyPublicKey } from "./encryptForCompany";
import { readAsBase64 } from "./readAsBase64";

// EBI, decyzja D1 (docs/ebi-plan.md) — powiązanie klucza obiektu adresata z doręczeniem EBI, podpisane KLUCZEM FIRMY.
// Nadawca podpisuje je przy nadaniu (kreator, „Generuj pisma”), a przeglądarka urzędu, która w tle przeszyfrowuje klucze
// dla petentów, sprawdza podpis kluczami firmy trzymanymi w pamięci karty. Bez ważnego podpisu niczego nie przeszyfruje —
// zapis w bazie nie wystarczy już, żeby zamówić u niej klucz dokumentu czy teczki firmy.
//
// Oświadczenie to UTF-8 JSON {v, t, ws, participant, key}; BE sprawdza je przy nadaniu (EbiKeyBinding.cs) i przechowuje
// w EbiDeliveries. W żądaniach jedzie jako base64 (tablica bajtów w C#) — globalny koder HTML BE zepsułby cudzysłowy.

export const EBI_KEY_BINDING_TYPE = "nota-ebi-key-binding";

/** Pola odbiorcy EBI w żądaniu nadania (base64). */
export interface IEbiKeyBindingFields {
  EbiKeyBinding: string;
  EbiKeyBindingSignature: string;
}

interface IEbiKeyBindingStatement {
  v: number;
  t: string;
  ws: string;
  participant: number;
  key: string;
}

export const base64ToBytes = (value: string): Uint8Array =>
  Uint8Array.from(atob(value), (c) => c.charCodeAt(0));

/** Klucz publiczny z base64 — binarny packetlist (firma, petent, obiekt) albo ASCII-armored. Rzuca przy błędzie. */
export const readPublicKey = async (base64: string): Promise<openpgp.key.Key> => {
  const bytes = base64ToBytes(base64);

  try {
    const armored = await openpgp.key.readArmored(new TextDecoder().decode(bytes));
    if (armored.keys.length) return armored.keys[0];
  } catch {
    // nie armored — odczyt binarny niżej
  }

  // openpgp 4.10: key.read nie rzuca przy błędzie — zwraca { keys: [], err }
  const binary = await openpgp.key.read(bytes);
  if (!binary.keys.length) throw new Error("Nie udało się odczytać klucza publicznego.");

  return binary.keys[0];
};

/** Podpis odłączony (binarny, base64) pod bajtami `data`. */
export const signDetachedBase64 = async (data: Uint8Array, privateKey: openpgp.key.Key): Promise<string> => {
  const { signature } = await openpgp.sign({
    message: openpgp.message.fromBinary(data),
    privateKeys: [privateKey],
    detached: true,
    armor: false,
  });

  return readAsBase64(signature.packets.write());
};

/** Klucz prywatny AKTYWNEJ generacji klucza firmy — z grantów odszyfrowanych w tej karcie. */
const activeCompanyPrivateKey = async (): Promise<openpgp.key.Key> => {
  const fingerprint = (await getCompanyPublicKey()).getFingerprint();
  const privateKey = getCompanyPrivateKeys().find((k) => k.getFingerprint() === fingerprint);

  if (!privateKey) {
    throw new Error(
      "Przesyłkę EBI nadaje pracownik z kluczem firmy — ta przeglądarka go nie ma. Odśwież aplikację albo poproś administratora o wydanie klucza.",
    );
  }

  return privateKey;
};

/** Oświadczenie nadawcy dla adresata EBI: klucz obiektu `objectPublicKey` jest kluczem przesyłki do podmiotu `participantId`. */
export const signEbiKeyBinding = async (
  objectPublicKey: openpgp.key.Key,
  participantId: number,
): Promise<IEbiKeyBindingFields> => {
  const { WorkspaceId } = await getActiveCompanyKey();

  const statement: IEbiKeyBindingStatement = {
    v: 1,
    t: EBI_KEY_BINDING_TYPE,
    ws: WorkspaceId,
    participant: participantId,
    key: objectPublicKey.getFingerprint(),
  };

  const bytes = new TextEncoder().encode(JSON.stringify(statement));

  return {
    EbiKeyBinding: await readAsBase64(bytes),
    EbiKeyBindingSignature: await signDetachedBase64(bytes, await activeCompanyPrivateKey()),
  };
};

/**
 * Przeglądarka urzędu przed przeszyfrowaniem: oświadczenie dotyczy tej firmy, tego podmiotu i dokładnie tego klucza
 * obiektu, a podpis składa się kluczem firmy trzymanym w tej karcie (dowolna generacja). Rzuca, gdy cokolwiek nie gra.
 */
export const verifyEbiKeyBinding = async (
  item: { KeyBinding?: string | null; KeyBindingSignature?: string | null; ParticipantId?: number | null },
  objectFingerprint: string,
  workspaceId: string,
  companyKeys: openpgp.key.Key[],
): Promise<void> => {
  if (!item.KeyBinding || !item.KeyBindingSignature) {
    throw new Error("Przesyłka bez podpisanego kluczem firmy powiązania z doręczeniem EBI — nie udostępniamy.");
  }

  const bytes = base64ToBytes(item.KeyBinding);
  let statement: Partial<IEbiKeyBindingStatement>;

  try {
    statement = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new Error("Nieczytelne powiązanie klucza przesyłki z doręczeniem EBI.");
  }

  const matches =
    statement.v === 1 &&
    statement.t === EBI_KEY_BINDING_TYPE &&
    (statement.ws ?? "").toLowerCase() === workspaceId.toLowerCase() &&
    typeof statement.participant === "number" &&
    statement.participant > 0 &&
    (item.ParticipantId == null || statement.participant === item.ParticipantId) &&
    (statement.key ?? "").toLowerCase() === objectFingerprint.toLowerCase();

  if (!matches) throw new Error("Powiązanie klucza nie dotyczy tej przesyłki.");

  const { signatures } = await openpgp.verify({
    message: openpgp.message.fromBinary(bytes),
    signature: await openpgp.signature.read(base64ToBytes(item.KeyBindingSignature)),
    publicKeys: companyKeys.map((k) => k.toPublic()),
  });

  const verified =
    signatures.length === 1 &&
    ((await Promise.resolve(signatures[0].verified).catch(() => false)) as unknown as boolean) === true;

  if (!verified) throw new Error("Nieprawidłowy podpis kluczem firmy pod powiązaniem klucza przesyłki.");
};
